import { Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { ExcelParserService } from '../../services/excel-parser.service';
import { ConsumptionHubService } from '../../services/consumption-hub.service';
import { ModelData, TargetValidationError } from '../../models/interfaces';

@Component({
  selector: 'app-publish-section',
  templateUrl: './publish-section.component.html'
})
export class PublishSectionComponent {
  @ViewChild('fallbackInput') fallbackInput!: ElementRef<HTMLInputElement>;

  /** 'model' = wells/wellbore/trajectory (Compass/Baker export); 'targets' = targets file. */
  importMode: 'model' | 'targets' = 'model';
  isOffShore = false;

  modelStatus = 'No file selected.';
  modelStatusClass = 'text-muted';
  publishStatus = '';
  publishStatusClass = '';
  publishing = false;
  modelLoaded = false;
  /** Parsed models queued for publish (one per imported file in multi-file mode). */
  pendingModels: Array<{ data: ModelData; fileName: string }> = [];
  private currentModel: ModelData | null = null;
  /** Total files in the last batch import (may exceed pendingModels.length when some fail to parse). */
  pendingTotal = 0;

  // Targets import state.
  targetsLoaded = false;
  targetsBlocked = false;
  checkingWellbores = false;
  unresolvedWells: string[] = [];
  resolutionWarnings: string[] = [];
  /** Row-level Consumption Hub Template validation errors (AC6/AC7); affected rows are excluded from the imported sets. */
  templateErrors: TargetValidationError[] = [];

  constructor(
    private excelParser: ExcelParserService,
    private hub: ConsumptionHubService,
    private ngZone: NgZone
  ) {}

  onImportModeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.importMode = value === 'targets' ? 'targets' : 'model';
    // Clear transient state when switching modes.
    this.publishStatus = '';
    this.publishStatusClass = '';
    this.pendingModels = [];
    this.pendingTotal = 0;
    this.modelLoaded = false;
    this.templateErrors = [];
  }

  onIsOffShoreChange(event: Event): void {
    this.isOffShore = (event.target as HTMLInputElement).checked;
    for (const pending of this.pendingModels) {
      pending.data.well.is_off_shore = this.isOffShore;
    }
    if (this.currentModel) {
      this.currentModel.well.is_off_shore = this.isOffShore;
      this.hub.importModel(this.currentModel);
    }
  }

  private applyWellEnvironment(model: ModelData): ModelData {
    model.well.is_off_shore = this.isOffShore;
    return model;
  }

  get publishLabel(): string {
    return this.importMode === 'targets' ? 'Publish Targets' : 'Publish Model';
  }

  /** Publish is blocked while busy, before an import, while wellbores are being checked, or when targets have unresolved wells. */
  get publishDisabled(): boolean {
    if (this.publishing) return true;
    return this.importMode === 'targets'
      ? (!this.targetsLoaded || this.checkingWellbores || this.targetsBlocked)
      : !this.modelLoaded;
  }

  async openFilePicker(): Promise<void> {
    const allowMultiple = this.importMode === 'model';
    if ('showOpenFilePicker' in window) {
      try {
        const fileHandles: FileSystemFileHandle[] = await (window as any).showOpenFilePicker({
          types: [
            {
              description: 'Excel Workbooks',
              accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls']
              }
            },
            {
              description: 'CSV Files',
              accept: { 'text/csv': ['.csv'] }
            }
          ],
          excludeAcceptAllOption: false,
          multiple: allowMultiple
        });
        const files = await Promise.all(fileHandles.map(h => h.getFile()));
        await this.ngZone.run(() => this.processFiles(files));
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          this.setModelStatus(`Import failed: ${err.message}`, 'error');
        }
      }
    } else {
      this.fallbackInput.nativeElement.click();
    }
  }

  async onFallbackFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    await this.processFiles(files);
    if (input) input.value = '';
  }

  private async processFiles(files: File[]): Promise<void> {
    if (this.importMode === 'targets') {
      if (files[0]) await this.processTargetsFile(files[0]);
      return;
    }
    if (files.length > 1) {
      await this.batchParseFiles(files);
    } else if (files[0]) {
      this.pendingModels = [];
      await this.processModelFile(files[0]);
    }
  }

  /**
   * For multi-file model imports: parse every file, accumulate results, then
   * show a summary and enable the Publish button. Publishing happens when the
   * user clicks Publish (same button, same flow) — each pending model is
   * imported and published sequentially.
   */
  private async batchParseFiles(files: File[]): Promise<void> {
    this.pendingModels = [];
    this.pendingTotal = 0;
    this.modelLoaded = false;
    this.publishStatus = '';
    this.publishStatusClass = '';
    const total = files.length;
    const parseErrors: string[] = [];
    const allWarnings: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.setModelStatus(`Parsing ${i + 1} of ${total}: ${file.name}…`);
      try {
        let model: ModelData;
        if (file.name.toLowerCase().endsWith('.csv')) {
          model = this.applyWellEnvironment(await this.excelParser.parseModelCsv(await file.text()));
        } else {
          model = this.applyWellEnvironment(await this.excelParser.parseModelWorkbook(await file.arrayBuffer()));
        }
        this.pendingModels.push({ data: model, fileName: file.name });
        if (model.warnings?.length) allWarnings.push(...model.warnings.map(w => `${file.name}: ${w}`));
      } catch (error: any) {
        const msg = error instanceof Error ? error.message : String(error);
        parseErrors.push(`${file.name}: ${msg}`);
      }
    }

    const ok = this.pendingModels.length;
    this.pendingTotal = total;
    this.modelLoaded = ok > 0;

    if (parseErrors.length === 0) {
      const warnNote = allWarnings.length ? ` (${allWarnings.length} warning${allWarnings.length > 1 ? 's' : ''})` : '';
      this.setModelStatus(`Parsed ${ok} of ${total} file${total > 1 ? 's' : ''}${warnNote}. Ready to publish.`, allWarnings.length ? 'warning' : 'success');
      if (allWarnings.length) {
        this.setPublishStatus(`Warnings: ${allWarnings.join(' | ')}`, 'warning');
      } else {
        this.setPublishStatus(`${ok} file${ok > 1 ? 's' : ''} ready to publish.`, 'success');
      }
    } else {
      this.setModelStatus(
        `Parsed ${ok} of ${total}; ${parseErrors.length} failed: ${parseErrors.join(', ')}`,
        ok > 0 ? 'warning' : 'error'
      );
      this.setPublishStatus(
        ok > 0 ? `${ok} file${ok > 1 ? 's' : ''} ready — fix failed files and re-import before publishing all.` : 'All files failed to parse.',
        ok > 0 ? 'warning' : 'error'
      );
    }
  }

  private async processModelFile(file: File): Promise<void> {
    const fileName = file.name;
    this.setModelStatus(`Reading ${fileName}…`);
    try {
      let model;
      if (fileName.toLowerCase().endsWith('.csv')) {
        const text = await file.text();
        model = this.applyWellEnvironment(await this.excelParser.parseModelCsv(text));
      } else {
        const arrayBuffer = await file.arrayBuffer();
        model = this.applyWellEnvironment(await this.excelParser.parseModelWorkbook(arrayBuffer));
      }
      this.currentModel = model;
      this.hub.importModel(model);
      this.modelLoaded = true;
      const stationCount = model?.trajectory?.stations?.length ?? 0;
      const stationText = stationCount ? ` (${stationCount} stations)` : '';
      const warnings = model?.warnings ?? [];
      if (warnings.length) {
        this.setModelStatus(`Imported model from ${fileName}${stationText}, with warnings.`, 'warning');
        this.setPublishStatus(`Imported with warnings — review before publishing: ${warnings.join(' ')}`, 'warning');
      } else {
        this.setModelStatus(`Imported model from ${fileName}${stationText}.`, 'success');
        this.setPublishStatus('Workbook imported. Ready to publish.', 'success');
      }
    } catch (error: any) {
      const message = error instanceof Error ? error.message : String(error);
      this.modelLoaded = false;
      this.setModelStatus(`Import failed: ${message}`, 'error');
      this.setPublishStatus(`Model import failed: ${message}`, 'error');
    }
  }

  private async processTargetsFile(file: File): Promise<void> {
    const fileName = file.name;
    this.setModelStatus(`Reading ${fileName}…`);
    this.targetsLoaded = false;
    this.targetsBlocked = false;
    this.unresolvedWells = [];
    this.resolutionWarnings = [];
    this.templateErrors = [];
    try {
      let targets;
      if (fileName.toLowerCase().endsWith('.csv')) {
        const text = await file.text();
        targets = await this.excelParser.parseTargetsCsv(text);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        targets = await this.excelParser.parseTargetsWorkbook(arrayBuffer);
      }
      this.templateErrors = targets.errors ?? [];
      const setCount = targets?.sets?.length ?? 0;

      if (setCount === 0) {
        this.targetsBlocked = true;
        this.setModelStatus(`Imported ${fileName}, but no valid wells were found — see the errors below.`, 'error');
        this.setPublishStatus('Fix the validation errors and re-import before publishing.', 'error');
        return;
      }

      this.hub.importTargets(targets);
      this.targetsLoaded = true;
      const setText = ` (${setCount} target set${setCount === 1 ? '' : 's'})`;
      const errorNote = this.templateErrors.length
        ? ` — ${this.templateErrors.length} validation issue(s) found (see details below)`
        : '';
      this.setModelStatus(
        `Imported targets from ${fileName}${setText}${errorNote}.`,
        this.templateErrors.length ? 'warning' : 'success'
      );

      await this.checkTargetWellbores();
    } catch (error: any) {
      const message = error instanceof Error ? error.message : String(error);
      this.targetsLoaded = false;
      this.setModelStatus(`Import failed: ${message}`, 'error');
      this.setPublishStatus(`Targets import failed: ${message}`, 'error');
    }
  }

  /** Resolve imported target wells to published wellbores; block + warn on any that do not resolve. */
  private async checkTargetWellbores(): Promise<void> {
    this.checkingWellbores = true;
    this.setPublishStatus('Validating wells and wellbores — please wait…');
    try {
      const resolution = await this.hub.resolveTargetWellbores();
      this.unresolvedWells = resolution?.unresolved ?? [];
      this.resolutionWarnings = resolution?.warnings ?? [];
      if (this.unresolvedWells.length) {
        this.targetsBlocked = true;
        this.setPublishStatus(
          `${this.unresolvedWells.length} well(s) do not match a published wellbore. ` +
          `See the list above — publish the well/wellbore first, then re-import.`,
          'warning'
        );
      } else {
        this.setPublishStatus('Targets imported. Ready to publish.', 'success');
      }
    } catch (error: any) {
      // Could not verify (e.g. not signed in): block publish until the user re-imports after sign-in.
      this.targetsBlocked = true;
      const detail = error instanceof Error ? ` Details: ${error.message}` : '';
      this.setPublishStatus(
        `Couldn't verify wellbores — sign in and re-import the file to check for unresolved wells before publishing.${detail}`,
        'warning'
      );
    } finally {
      this.checkingWellbores = false;
    }
  }

  async publish(): Promise<void> {
    if (this.importMode === 'targets') {
      return this.publishTargets();
    }
    return this.publishModel();
  }

  async publishModel(): Promise<void> {
    if (!this.modelLoaded) {
      this.setPublishStatus('No model loaded. Please import a file before publishing.', 'error');
      return;
    }
    this.publishing = true;

    // Multi-file batch: publish each pending model in sequence.
    if (this.pendingModels.length > 0 && this.pendingTotal > 1) {
      const total = this.pendingTotal;
      let succeeded = 0;
      const errors: string[] = [];

      for (let i = 0; i < this.pendingModels.length; i++) {
        const { data, fileName } = this.pendingModels[i];
        this.setPublishStatus(`Publishing ${i + 1} of ${total}: ${fileName}…`);
        try {
          this.hub.importModel(data);
          await this.hub.publishFullModel();
          succeeded++;
        } catch (error: any) {
          errors.push(`${fileName}: ${(error instanceof Error ? error.message : String(error))}`);
        }
      }

      this.publishing = false;
      if (errors.length === 0) {
        this.setPublishStatus(`Published ${succeeded} of ${total} files successfully.`, 'success');
      } else {
        this.setPublishStatus(
          `${succeeded} of ${total} published. Failed: ${errors.join(' | ')}`,
          errors.length === total ? 'error' : 'warning'
        );
      }
      return;
    }

    // Single-file: import the pending model (if any) then publish.
    if (this.pendingModels.length === 1) {
      this.hub.importModel(this.pendingModels[0].data);
    }
    this.setPublishStatus('Publishing model…');
    try {
      await this.hub.publishFullModel();
      this.setPublishStatus('Published successfully — well, wellbore, and trajectory uploaded.', 'success');
    } catch (error: any) {
      this.setPublishStatus(`Publish error: ${error.message}`, 'error');
    } finally {
      this.publishing = false;
    }
  }

  async publishTargets(): Promise<void> {
    if (!this.targetsLoaded) {
      this.setPublishStatus('No targets loaded. Please import a targets file before publishing.', 'error');
      return;
    }
    if (this.targetsBlocked) {
      this.setPublishStatus(
        `Cannot publish: ${this.unresolvedWells.length} well(s) do not match a published wellbore. ` +
        `Publish the well/wellbore first, then re-import.`,
        'error'
      );
      return;
    }
    this.publishing = true;
    this.setPublishStatus('Publishing targets…');

    try {
      const result = await this.hub.publishTargets();
      this.setPublishStatus('Targets published successfully.', 'success');
    } catch (error: any) {
      this.setPublishStatus(`Publish error: ${error.message}`, 'error');
    } finally {
      this.publishing = false;
    }
  }

  private setModelStatus(text: string, type: 'success' | 'error' | 'warning' | '' = ''): void {
    this.modelStatus = text;
    switch (type) {
      case 'success': this.modelStatusClass = 'text-success'; break;
      case 'error': this.modelStatusClass = 'text-danger'; break;
      case 'warning': this.modelStatusClass = 'text-warning'; break;
      default: this.modelStatusClass = 'text-muted'; break;
    }
  }

  private setPublishStatus(text: string, type: 'success' | 'error' | 'warning' | '' = ''): void {
    this.publishStatus = text;
    switch (type) {
      case 'success': this.publishStatusClass = 'status-ok'; break;
      case 'error': this.publishStatusClass = 'status-error'; break;
      case 'warning': this.publishStatusClass = 'text-warning'; break;
      default: this.publishStatusClass = ''; break;
    }
  }

  /** Bootstrap alert variant for the boxed publish status, derived from the status type. */
  get publishStatusAlertClass(): string {
    switch (this.publishStatusClass) {
      case 'status-ok': return 'alert-success';
      case 'status-error': return 'alert-danger';
      case 'text-warning': return 'alert-warning';
      default: return 'alert-secondary';
    }
  }
}
